import _, { concat, difference, filter, includes, noop, xor } from 'lodash';
import React, { createContext, PropsWithChildren, Reducer, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { gql, useQuery } from 'urql';
import { DeepPartialGraph } from '../types';
import { Adr, HacpackQueryUnNumbersArguments } from '../types/graph';

type UnNumbersQueryData = { unNumbers: DeepPartialGraph<Adr>[] }

const unNumbersQuery = gql<UnNumbersQueryData, HacpackQueryUnNumbersArguments>`
    query unNumbers($unNumbers: [String!]!) {
        unNumbers(unNumbers:$unNumbers) {
            id
            un
            packingGroup
            hazardCls
        }
    }
`;

export type UnNumbersContextObject = {
    data?: UnNumbersQueryData | null,
    fetchingUnNumbers: string[],
    fetchedUnNumbers: string[],
    load: (unNumbers: string[]) => void
};

const context = createContext<UnNumbersContextObject>({
    data: null,
    fetchingUnNumbers: [],
    fetchedUnNumbers: [],
    load: noop
});

export type UnNumbersContextProps = PropsWithChildren<{
    /*lazy?: boolean*/
}>;

export type UseUnNumbersProps = {
    unNumbers?: string[] | null
};

export const useUnNumbers = (props: UseUnNumbersProps | undefined = undefined) => {
    const { data: contextData, fetchingUnNumbers, fetchedUnNumbers, load } = useContext(context);
    //const lazy = props?.lazy ?? false;

    const unNumbers = props?.unNumbers;
    const [data, setData] = useState<UnNumbersQueryData|undefined>(undefined);

    const newUnNumbers = useMemo(() => {
        if (unNumbers === undefined || unNumbers === null || unNumbers.length === 0) {
            return null;
        }

        const diff = difference(unNumbers, concat(fetchingUnNumbers, fetchedUnNumbers));

        if (diff.length === 0) {
            return null;
        }

        return diff;
    }, [unNumbers, fetchingUnNumbers, fetchedUnNumbers]);

    const fetching = useMemo(() => difference(unNumbers, fetchedUnNumbers).length === 0, [unNumbers, fetchedUnNumbers]);

    useEffect(() => {
        if (contextData === undefined || contextData === null) {
            return;
        }

        setData(data => {
            if (unNumbers === undefined) {
                return contextData;
            }

            const filteredUnNumbers = filter(contextData?.unNumbers, ({ un }) => includes(unNumbers, un));

            if (data !== undefined && xor(filteredUnNumbers, data?.unNumbers).length === 0) {
                return data;
            }

            return {
                ...(data??contextData),
                unNumbers: filteredUnNumbers
            }
        })
    }, [contextData, unNumbers, setData]);

    useEffect(() => {
        if (newUnNumbers === null) {
            return;
        }

        load(newUnNumbers);
    }, [newUnNumbers]);

    return { data, fetching };
}

type UnNumbersReducerState = {
    queued: string[],
    loading: string[],
    loaded: string[]
};

type UnNumbersReducerAction = { type: 'load', payload: { unNumbers: string[]/*, reload?: boolean*/ } } | { type: 'reset' | 'next' };

type UnNumbersReducer = Reducer<UnNumbersReducerState, UnNumbersReducerAction>

const initUnNumbersReducer = (): UnNumbersReducerState => ({
    queued: [],
    loading: [],
    loaded: []
});

const unNumbersReducer: UnNumbersReducer = (prevState, action) => {
    const { queued, loading, loaded } = prevState;

    switch (action.type) {
        case 'reset':
            return initUnNumbersReducer();
        case 'load':
            const unNumbers = difference(action.payload.unNumbers, loaded);

            if (loading.length === 0) {
                return { ...prevState, loading: unNumbers, }
            }

            return { ...prevState, queued: _(unNumbers).difference(loading).union(queued).value() };
        case 'next':
            return { ...prevState, queued: [], loading: queued, loaded: [...loaded, ...loading] }
        default:
            throw new Error();
    }
}

export const UnNumbersContext = ({ children/*, lazy = false*/ }: UnNumbersContextProps) => {

    const [{ queued, loading: unNumbers, loaded }, dispatch] = useReducer(unNumbersReducer, null, initUnNumbersReducer);

    const [data, setData] = useState<UnNumbersQueryData | null | undefined>(undefined);

    const [queryResult] = useQuery<UnNumbersQueryData, HacpackQueryUnNumbersArguments>({
        query: unNumbersQuery,
        variables: { unNumbers },
        pause: unNumbers.length === 0
    });

    const { data: resultData, fetching } = queryResult;

    const [fetchingUnNumbers, setFetchingUnNumbers] = useState<string[]>([...queued, ...unNumbers]);

    useEffect(() => {
        setFetchingUnNumbers(oldVal => {
            const newVal = [...queued, ...unNumbers];

            if (xor(newVal, oldVal).length === 0) {
                return oldVal;
            }

            return newVal;
        });
    }, [queued, unNumbers, setFetchingUnNumbers])

    useEffect(() => {
        if (fetching === true) {
            return;
        }

        dispatch({ type: 'next' });

        if (resultData === undefined) {
            return;
        }

        if (resultData.unNumbers.length === 0) {
            return;
        }

        setData(data => {
            if (data === undefined || data === null) {
                return resultData;
            }

            return {
                ...data,
                unNumbers: [...resultData.unNumbers, ...data.unNumbers]
            }
        });

    }, [fetching, resultData, setData, dispatch])

    const load = useCallback((unNumbers: string[]) => dispatch({ type: 'load', payload: { unNumbers } }), []);

    return <context.Provider value={{ data, load, fetchingUnNumbers, fetchedUnNumbers: loaded }}>{children}</context.Provider>;
};
